import * as React from "react";
import { X, Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MasterSelect - Multi-select dropdown that fetches options from master database
 * 
 * @param {String} masterDb - Name of the master database (skills, languages, degrees, institutions, etc.)
 * @param {Array} selected - Array of selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {String} placeholder - Placeholder text
 * @param {Boolean} allowCustom - Allow adding custom values
 * @param {String} className - Additional CSS classes
 */
export function MasterSelect({
  masterDb,
  selected = [],
  onChange,
  placeholder = "Select items...",
  allowCustom = true,
  className,
  maxDisplayed = 10,
  tagVariant = "default",
  token = null, // Auth token for authenticated requests
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch options from master database
  const fetchOptions = React.useCallback(async (search = "") => {
    setLoading(true);
    try {
      const endpoint = token 
        ? `${API_URL}/api/master/${masterDb}?search=${encodeURIComponent(search)}&limit=50`
        : `${API_URL}/api/public/master/${masterDb}/search?q=${encodeURIComponent(search)}&limit=50`;
      
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(endpoint, { headers });
      const data = await response.json();
      setOptions((data.items || []).map(item => ({
        value: item.name,
        label: item.name,
        id: item.id
      })));
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      setOptions([]);
    }
    setLoading(false);
  }, [masterDb, token]);

  // Initial load and debounced search
  React.useEffect(() => {
    if (open) {
      fetchOptions(inputValue);
    }
  }, [open, fetchOptions]);

  // Debounced search
  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      fetchOptions(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, open, fetchOptions]);

  const handleUnselect = (item) => {
    onChange(selected.filter((s) => s !== item));
  };

  const handleSelect = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
    setInputValue("");
  };

  const handleAddCustom = async () => {
    const trimmed = inputValue.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onChange([...selected, trimmed]);
      
      // Optionally add to master DB
      if (token) {
        try {
          await fetch(`${API_URL}/api/master/${masterDb}?name=${encodeURIComponent(trimmed)}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (e) {
          console.log('Could not add to master DB:', e);
        }
      }
      
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && allowCustom && inputValue.trim()) {
      e.preventDefault();
      handleAddCustom();
    }
    if (e.key === "Backspace" && !inputValue && selected.length > 0) {
      handleUnselect(selected[selected.length - 1]);
    }
  };

  const getTagStyles = () => {
    switch (tagVariant) {
      case "outline":
        return "bg-transparent border-2 border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950";
      case "secondary":
        return "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200";
      case "colorful":
        return "bg-gradient-to-r from-blue-500 to-purple-500 text-white";
      case "language":
        return "bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/50 dark:text-purple-300";
      case "skill":
        return "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300";
      case "certification":
        return "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/50 dark:text-amber-300";
      default:
        return "bg-blue-500 text-white hover:bg-blue-600";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between min-h-[44px] h-auto py-2 px-3 font-normal"
          >
            <span className="text-muted-foreground truncate">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[300px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${masterDb}...`}
              value={inputValue}
              onValueChange={setInputValue}
              onKeyDown={handleKeyDown}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : options.length === 0 ? (
                <CommandEmpty>
                  {allowCustom && inputValue.trim() ? (
                    <button
                      onClick={handleAddCustom}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-accent rounded cursor-pointer"
                    >
                      <Plus className="h-4 w-4" />
                      Add "{inputValue.trim()}"
                    </button>
                  ) : (
                    <span className="text-muted-foreground px-4 py-2 block">
                      {inputValue ? "No matches found." : `Type to search ${masterDb}`}
                    </span>
                  )}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.id || option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected.includes(option.value) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                  {allowCustom && inputValue.trim() && !options.find(o => o.value.toLowerCase() === inputValue.trim().toLowerCase()) && (
                    <CommandItem
                      onSelect={handleAddCustom}
                      className="cursor-pointer text-blue-600"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add "{inputValue.trim()}"
                    </CommandItem>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Tags Display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50/50 dark:bg-slate-800/50 min-h-[48px]">
          {selected.slice(0, maxDisplayed).map((item) => (
            <Badge
              key={item}
              className={cn(
                "px-2.5 py-1 text-xs font-medium cursor-pointer transition-all",
                "flex items-center gap-1.5 rounded-md shadow-sm",
                getTagStyles()
              )}
              onClick={() => handleUnselect(item)}
            >
              {item}
              <X className="h-3 w-3 opacity-70 hover:opacity-100" />
            </Badge>
          ))}
          {selected.length > maxDisplayed && (
            <Badge variant="outline" className="px-2.5 py-1 text-xs">
              +{selected.length - maxDisplayed} more
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default MasterSelect;
